import React, { useState } from "react";
import {
  Table,
  Button,
  Form,
  Input,
  Popconfirm,
  message,
  Space,
  Divider,
  Tag,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  useVariantsQuery,
  useSaveVariantMutation,
  useDeleteVariantMutation,
} from "../../Services";
import { drawerFooter, drawerTitle } from "../AppUi";
import { ResizableDrawer } from "../ResizableDrawer/ResizableDrawer";
import { MasterProductVariant, VariantOption } from "../../Types";

interface VariantDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const VariantDrawer: React.FC<VariantDrawerProps> = ({
  open,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [editingItem, setEditingItem] = useState<MasterProductVariant | null>(null);
  const [optionInput, setOptionInput] = useState("");
  const [currentOptions, setCurrentOptions] = useState<VariantOption[]>([]);

  const { data: variants = [], isLoading } = useVariantsQuery({
    enabled: open,
  });

  const saveMutation = useSaveVariantMutation();
  const deleteMutation = useDeleteVariantMutation();

  const handleStartEdit = (record: MasterProductVariant) => {
    setEditingItem(record);
    form.setFieldsValue({
      variant_name: record.variant_name,
    });
    setCurrentOptions(record.variant_types || []);
    setOptionInput("");
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    form.resetFields();
    setCurrentOptions([]);
    setOptionInput("");
  };

  const handleAddOption = () => {
    const trimmed = optionInput.trim();
    if (!trimmed) return;
    if (currentOptions.some((opt) => opt.name.toLowerCase() === trimmed.toLowerCase())) {
      message.warning("Option value already exists");
      return;
    }
    setCurrentOptions((prev) => [
      ...prev,
      { id: `opt-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`, name: trimmed },
    ]);
    setOptionInput("");
  };

  const handleRemoveOption = (idToRemove: string) => {
    setCurrentOptions((prev) => prev.filter((opt) => opt.id !== idToRemove));
  };

  const handleSave = async (shouldClose = true) => {
    try {
      let values: { variant_name?: string } | null = null;
      try {
        values = await form.validateFields();
      } catch (err: any) {
        const firstError = err?.errorFields?.[0]?.errors?.[0];
        message.error(firstError || "Please enter a valid variant name.");
        return;
      }

      const variantName = (values?.variant_name || "").trim();
      if (!variantName) {
        message.error("Please enter a variant name.");
        return;
      }

      let optionsToSave = [...currentOptions];
      const trimmedInput = optionInput.trim();
      if (
        trimmedInput &&
        !optionsToSave.some(
          (opt) => opt.name.toLowerCase() === trimmedInput.toLowerCase()
        )
      ) {
        const newOpt = {
          id: `opt-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
          name: trimmedInput,
        };
        optionsToSave.push(newOpt);
        setCurrentOptions(optionsToSave);
        setOptionInput("");
      }

      if (optionsToSave.length === 0) {
        message.error("Please add at least one option value (e.g. Small, Medium).");
        return;
      }

      saveMutation.mutate(
        {
          id: editingItem?.id,
          variant_name: variantName,
          variant_types: optionsToSave,
        },
        {
          onSuccess: (res) => {
            if (res?.success) {
              message.success(
                editingItem
                  ? "Product variant schema updated!"
                  : "Product variant schema created!"
              );
              handleCancelEdit();
              if (shouldClose) {
                onClose();
              }
            } else {
              message.error(res?.message || "Failed to save variant.");
            }
          },
          onError: (err: any) => {
            message.error(err?.message || "Something went wrong saving variant.");
          },
        }
      );
    } catch (e: any) {
      console.error("Error saving variant:", e);
      message.error(e?.message || "Something went wrong.");
    }
  };

  const handleDelete = async (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: (res) => {
        if (res?.success) {
          message.success("Product variant deleted successfully!");
        } else {
          message.error(res?.message || "Failed to delete variant.");
        }
      },
      onError: () => message.error("Something went wrong."),
    });
  };

  const columns = [
    {
      title: "Variant Name",
      dataIndex: "variant_name",
      key: "variant_name",
      width: 140,
      render: (text: string) => (
        <span className="font-bold text-slate-800 text-xs">{text}</span>
      ),
    },
    {
      title: "Option Values",
      dataIndex: "variant_types",
      key: "variant_types",
      render: (opts: VariantOption[]) => (
        <div className="flex flex-wrap gap-1">
          {(opts || []).map((opt) => (
            <Tag key={opt.id} color="blue" className="text-[11px] m-0 rounded-md font-medium">
              {opt.name}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      render: (_: any, record: MasterProductVariant) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined className="text-blue-600" />}
            size="small"
            onClick={() => handleStartEdit(record)}
          />
          <Popconfirm
            title="Delete this variant schema?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <ResizableDrawer
      title={drawerTitle(editingItem ? "Edit Product Variant Schema" : "Manage Product Variants")}
      open={open}
      onClose={() => {
        handleCancelEdit();
        onClose();
      }}
      width={560}
      footer={drawerFooter({
        actions: [
          {
            label: "Close",
            onClick: () => {
              handleCancelEdit();
              onClose();
            },
          },
          ...(editingItem
            ? [
                {
                  label: "Cancel Edit",
                  onClick: handleCancelEdit,
                },
              ]
            : []),
          {
            label: editingItem ? "Update Variant" : "Save Variant",
            type: "primary",
            icon: <SaveOutlined />,
            loading: saveMutation.isPending,
            onClick: () => handleSave(true),
          },
        ],
      })}
    >
      <div
        className="p-3 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-slate-200/80 dark:border-slate-700/60 my-3"
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
      >
        <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-200 mb-2">
          {editingItem ? `Editing Schema: ${editingItem.variant_name}` : "Create New Variant Schema"}
        </h4>
        <Form
          form={form}
          layout="vertical"
          onFinish={() => handleSave(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && optionInput.trim()) {
              e.preventDefault();
              e.stopPropagation();
              handleAddOption();
            }
          }}
        >
          <Form.Item
            label="Variant Name (e.g. Size, Color, Capacity)"
            name="variant_name"
            rules={[{ required: true, message: "Required" }]}
            style={{ marginBottom: 12 }}
          >
            <Input
              placeholder="e.g. Size, Color, Material"
              size="middle"
              className="rounded-lg"
              onKeyDown={(e) => e.stopPropagation()}
            />
          </Form.Item>

          <div className="flex flex-col gap-1.5 mb-1">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
              Option Values (e.g. Small, Medium, Large, Red, Green)
            </label>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Type an option and press Enter (e.g. Small)"
                value={optionInput}
                size="middle"
                onChange={(e) => setOptionInput(e.target.value)}
                onPressEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (optionInput.trim()) {
                    handleAddOption();
                  } else {
                    handleSave(true);
                  }
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
                className="rounded-lg flex-1"
              />
              <Button
                type="dashed"
                size="middle"
                icon={<PlusOutlined />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAddOption();
                }}
              >
                Add Value
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2 min-h-[32px] p-2 bg-white dark:bg-slate-900/40 rounded-lg border border-slate-200/60 dark:border-slate-700/50">
              {currentOptions.length === 0 ? (
                <span className="text-[11px] text-slate-400">No option values added yet.</span>
              ) : (
                currentOptions.map((opt) => (
                  <Tag
                    key={opt.id}
                    closable
                    onClose={() => handleRemoveOption(opt.id)}
                    color="blue"
                    className="text-xs rounded-md py-0.5"
                  >
                    {opt.name}
                  </Tag>
                ))
              )}
            </div>
          </div>
        </Form>
      </div>

      <Divider style={{ margin: "12px 0" }} />

      <Table
        dataSource={variants}
        columns={columns}
        rowKey="id"
        size="small"
        loading={isLoading}
        pagination={{ pageSize: 5, size: "small", showSizeChanger: false }}
        bordered
      />
    </ResizableDrawer>
  );
};
